import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";
import { buildQueryString } from "@/shared/utils/buildQueryString";

import { IDENTITIES_API_ENDPOINTS } from "../constants/identities.constants";
import type {
  IdentitiesApiResponse,
  IdentitiesCountsResponse,
  IdentitiesFilters,
  Identity,
  RegenerateKycLinkResponse,
  SendKycVerificationMailRequest,
} from "../types/identities-api.types";
import type { CreateIdentityRequest, CreateIdentityResponseDto } from "../types/identity-form.types";

export class IdentitiesService {
  static async fetchIdentities(filters: IdentitiesFilters): Promise<AxiosResponse<IdentitiesApiResponse>> {
    const url = buildQueryString(IDENTITIES_API_ENDPOINTS.IDENTITIES, filters);

    return HttpApiService.get<IdentitiesApiResponse>(url);
  }

  /**
   * Row counts per KYC bucket for the given filters. `kyc_group` is accepted but ignored by the endpoint —
   * every chip has to report its own bucket — so callers should leave it out.
   */
  static async fetchIdentitiesCounts(
    filters: Omit<IdentitiesFilters, "kyc_group" | "limit" | "skip" | "with_aggregates">,
  ): Promise<AxiosResponse<IdentitiesCountsResponse>> {
    const url = buildQueryString(IDENTITIES_API_ENDPOINTS.IDENTITIES_COUNTS, filters);

    return HttpApiService.get<IdentitiesCountsResponse>(url);
  }

  static async createIdentity(
    data: CreateIdentityRequest,
  ): Promise<AxiosResponse<CreateIdentityResponseDto>> {
    return HttpApiService.post<CreateIdentityResponseDto>(IDENTITIES_API_ENDPOINTS.IDENTITIES, data);
  }

  static async getIdentityById(
    id: string,
    options?: { withAggregates?: boolean },
  ): Promise<AxiosResponse<Identity>> {
    const qs = options?.withAggregates ? "?with_aggregates=true" : "";
    return HttpApiService.get<Identity>(`${IDENTITIES_API_ENDPOINTS.IDENTITIES}/${id}${qs}`);
  }

  static async regenerateKycLink(id: string): Promise<AxiosResponse<RegenerateKycLinkResponse>> {
    return HttpApiService.post<RegenerateKycLinkResponse, Record<string, never>>(
      IDENTITIES_API_ENDPOINTS.REGENERATE_KYC_LINK(id),
      {},
    );
  }

  static async sendKycVerificationMail(
    body: SendKycVerificationMailRequest,
  ): Promise<AxiosResponse<unknown>> {
    return HttpApiService.post<unknown, SendKycVerificationMailRequest>(
      IDENTITIES_API_ENDPOINTS.MAIL_SEND,
      body,
    );
  }
}
